
import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
} from '@chakra-ui/react';
import { CategorySelector } from './CategorySelector';
import { PlaidTransaction } from '../../types/transactions';

interface EditTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: PlaidTransaction | null;
  onSave: (id: string, updates: { merchant?: string; pfc_primary?: string; pfc_detailed?: string }) => Promise<void>;
  availableCategories: { primary: string; detailed: string; }[];
}

export const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onSave,
  availableCategories,
}) => {
  const [merchant, setMerchant] = useState('');
  const [pfcPrimary, setPfcPrimary] = useState('');
  const [pfcDetailed, setPfcDetailed] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize state when transaction changes or modal opens
  useEffect(() => {
    if (transaction) {
      setMerchant(transaction.merchantName || transaction.name || '');
      setPfcPrimary(transaction.personalFinanceCategoryPrimary || '');
      setPfcDetailed(transaction.personalFinanceCategoryDetailed || '');
    }
  }, [transaction, isOpen]);

  const handleSave = async () => {
    if (!transaction) return;

    setIsSaving(true);
    try {
      await onSave(transaction.id, {
        merchant: merchant,
        pfc_primary: pfcPrimary,
        pfc_detailed: pfcDetailed,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save transaction', error);
      // Ideally handle error display here or let parent handle it
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="bg.primary" color="text.primary" borderColor="xacto.borderGray" borderWidth="1px">
        <ModalHeader>Edit Transaction</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <FormLabel color="text.secondary" fontSize="sm">Merchant</FormLabel>
              <Input
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                bg="bg.surface"
                borderColor="xacto.borderGray"
                _focus={{ borderColor: 'xacto.electricCyan', boxShadow: 'none' }}
              />
            </FormControl>

            <CategorySelector
                initialPrimary={pfcPrimary}
                initialDetailed={pfcDetailed}
                availableCategories={availableCategories}
                onChange={(p, d) => {
                    setPfcPrimary(p);
                    setPfcDetailed(d);
                }}
            />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} color="text.secondary" _hover={{ bg: 'whiteAlpha.100' }}>
            Cancel
          </Button>
          <Button
            colorScheme="cyan"
            onClick={handleSave}
            isLoading={isSaving}
            bg="xacto.electricCyan"
            color="black"
            _hover={{ bg: 'cyan.300' }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};


